import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../../components/SEO';
import { PlansAPI } from '../../services/api';

type PlanType = 'individual' | 'institutional' | 'enterprise';

// UI tab key → actual backend `plan_type` value (backend uses 'institution' / 'company')
const PLAN_TYPE_MAP: Record<PlanType, string> = {
  individual: 'individual',
  institutional: 'institution',
  enterprise: 'company',
};

const UNCAPPED = 999999;

interface Plan {
  plan_id: string | number;
  name: string;
  plan_type: string;
  billing_cycle: string;
  price: number;
  amount: number;
  max_users: number;
  max_papers: number;
  gst_amount: number;
  total_amount: number;
  currency_code: string;
  currency_symbol: string;
  show_gst: boolean;
  trial_days: number;
  papers_per_month: string | null;
  papers_per_year: string | null;
  words_per_month: string | null;
  papers_approx: string | null;
  ai_detection: boolean;
  full_report: boolean;
  lms_integration: boolean;
  rest_api: boolean;
  webhook: boolean;
  batch_processing: boolean;
  priority_processing: boolean;
  white_label: boolean;
  sla: boolean;
  support: string;
  dedicated_manager: boolean;
  contact_sales: boolean;
  is_contact_sales: boolean;
  display_order: number;
  savings_note: string | null;
}

interface Currency {
  code: string;
  symbol: string;
  show_gst: boolean;
}

function formatWords(words: string) {
  const n = parseInt(words);
  if (n >= 10000000) return `${n / 10000000}Cr words/mo`;
  if (n >= 100000) return `${n / 100000}L words/mo`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K words/mo`;
  return `${n} words/mo`;
}

// no thousands separators — plain number rendering
function formatAmount(n: number) {
  return `${Math.round(n)}`;
}

function FeatureItem({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2.5 py-1">
      <svg className="w-3.5 h-3.5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
      </svg>
      <span className="text-[13px] text-gray-600 leading-snug">{text}</span>
    </div>
  );
}

function PlanCard({ plan, sym, tab }: { plan: Plan; sym: string; tab: PlanType }) {
  const isFree = plan.amount === 0 && !plan.is_contact_sales;
  const isContact = plan.is_contact_sales;
  const isAnnual = plan.billing_cycle === 'annual' || plan.billing_cycle === 'Annual';
  const periodLabel =
    plan.billing_cycle === 'one_time' || plan.billing_cycle === 'lifetime'
      ? 'forever'
      : isAnnual
        ? '/yr'
        : '/mo';

  const currencySym = plan.currency_symbol || sym;

  const hasLegacyVolumeField = !!(plan.papers_per_month || plan.papers_per_year || plan.words_per_month);

  const volumeLabel = plan.papers_per_month
    ? `${plan.papers_per_month} papers/month`
    : plan.papers_per_year
      ? `${plan.papers_per_year} papers/year`
      : plan.words_per_month && plan.words_per_month !== 'custom'
        ? formatWords(plan.words_per_month)
        : plan.words_per_month === 'custom'
          ? 'Custom volume'
          : plan.max_papers >= UNCAPPED
            ? 'Unlimited papers'
            : plan.max_papers
              ? `${plan.max_papers} papers`
              : null;

  const features: string[] = [];

  if (hasLegacyVolumeField) {
    if (plan.papers_per_month === '1' && isFree) features.push('1 check — lifetime (AI + Plagiarism)');
    if (plan.papers_per_month && !isFree) features.push(`${plan.papers_per_month} checks per month (AI + Plagiarism)`);
    if (plan.papers_per_year) features.push(`${plan.papers_per_year} checks per year (AI + Plagiarism)`);
    if (plan.words_per_month && plan.words_per_month !== 'custom') features.push(formatWords(plan.words_per_month));
    if (plan.words_per_month === 'custom') features.push('Custom word volume');
  } else {
    if (plan.max_papers === 1 && isFree) features.push('1 check — lifetime (AI + Plagiarism)');
    else if (plan.max_papers >= UNCAPPED) features.push('Unlimited checks (AI + Plagiarism)');
    else if (plan.max_papers) features.push(`Up to ${plan.max_papers} checks (AI + Plagiarism)`);

    if (plan.max_users >= UNCAPPED) features.push('Unlimited users');
    else if (plan.max_users) features.push(`Up to ${plan.max_users} user${plan.max_users > 1 ? 's' : ''}`);
  }

  if (plan.trial_days > 0) features.push(`${plan.trial_days}-day free trial`);
  // if (plan.ai_detection) features.push('AI content detection');
  if (plan.full_report) features.push('Full similarity report');
  if (plan.lms_integration) features.push('LMS integration (Moodle/Canvas)');
  if (plan.rest_api) features.push('REST API access');
  if (plan.webhook) features.push('Webhook notifications');
  if (plan.batch_processing) features.push('Batch processing');
  if (plan.priority_processing) features.push('Priority processing');
  if (plan.white_label) features.push('White-label option');
  if (plan.sla) features.push('SLA guarantee');
  if (plan.dedicated_manager) features.push('Dedicated account manager');
  if (plan.support === 'phone') features.push('Phone support');
  if (plan.support === 'dedicated') features.push('Dedicated support');
  else if (plan.support === 'email') features.push('Email support');

  return (
    <div className="relative flex flex-col rounded-2xl border-2 overflow-hidden transition-all border-gray-100 hover:border-gray-200 hover:shadow-md">
      {plan.savings_note && (
        <div className="absolute top-3 right-3 bg-green-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full">
          {plan.savings_note}
        </div>
      )}

      {/* Header */}
      <div className="p-5 bg-white">
        <p className="text-[11px] font-black uppercase tracking-widest mb-3 text-navy">
          {plan.name}
        </p>

        {isContact ? (
          <>
            <p className="text-[32px] font-black leading-none mb-1 text-gray-900">Custom</p>
            <p className="text-[12px] font-medium text-gray-400">Contact our sales team</p>
          </>
        ) : isFree ? (
          <>
            <p className="text-[32px] font-black leading-none mb-1 text-gray-900">{currencySym}0</p>
            <p className="text-[12px] font-medium text-gray-400">forever</p>
          </>
        ) : (
          <>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-[32px] font-black leading-none text-gray-900">
                {currencySym}{formatAmount(plan.price ?? 0)}
              </span>
              <span className="text-[13px] font-medium text-gray-400">
                {periodLabel}
              </span>
            </div>
            {plan.show_gst && (
              <p className="text-[11px] font-medium text-gray-400">
                + GST (18%) = {currencySym}{formatAmount(plan.total_amount ?? 0)} total
              </p>
            )}
            {plan.trial_days > 0 && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-green-50 text-green-700">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                {plan.trial_days}-day free trial
              </div>
            )}
          </>
        )}

        {volumeLabel && (
          <div className="mt-3 inline-block px-3 py-1.5 rounded-lg text-[11px] font-bold bg-blue-50 text-navy">
            {volumeLabel}
          </div>
        )}
      </div>

      {/* Features */}
      <div className="flex-1 p-5 bg-white border-t border-gray-100">
        <div className="flex flex-col">
          {features.map((f, i) => <FeatureItem key={i} text={f} />)}
        </div>
      </div>

      {/* CTA */}
      <div className="p-5 bg-white border-t border-gray-100">
        <Link
          to={isContact ? '/contact?inquiry=sales' : '/register'}
          state={isContact ? undefined : { role: tab === 'institutional' ? 'institution' : tab === 'enterprise' ? 'publisher' : 'researcher' }}
          className={`block text-center py-3 rounded-md text-[13px] font-black uppercase tracking-tight transition-colors ${isContact
            ? 'bg-gray-900 text-white hover:bg-gray-800'
            : isFree
              ? 'border-2 border-gray-200 text-gray-700 hover:bg-gray-50'
              : 'bg-navy text-white hover:bg-navy-dark'
            }`}
        >
          {isContact ? 'Contact sales' : isFree ? 'Get started free' : plan.trial_days > 0 ? `Start ${plan.trial_days}-day trial` : 'Get started'}
        </Link>
      </div>
    </div>
  );
}

const TAB_LABELS: { key: PlanType; label: string }[] = [
  { key: 'individual', label: 'Individual' },
  { key: 'institutional', label: 'Institutional' },
  { key: 'enterprise', label: 'Publisher' },
];

const faq = [
  { q: 'What counts as one check?', a: 'One submission regardless of length (within plan word limits). PDF and DOCX supported up to 50MB.' },
  { q: 'How does the free trial work?', a: 'Every paid plan includes a free trial: 7 days for individual, 30 days for institutional, 14 days for API. No credit card required to start.' },
  { q: 'Is GST included in the price?', a: 'Prices shown are exclusive of GST (18%) for Indian customers.' },
  { q: 'Can I upgrade or downgrade?', a: 'Yes, at any time. Upgrades take effect immediately. Downgrades apply at the next billing cycle.' },
  { q: 'How are institutional plans billed?', a: 'Institutional plans are billed annually in advance. We issue a formal invoice and support purchase orders.' },
  { q: 'Do unused checks roll over?', a: 'No — monthly checks reset on the 1st of each month. Annual checks are pooled across the year.' },
];

export default function Pricing() {
const location = useLocation();
  const navState = location.state as { tab?: PlanType; lockTab?: boolean } | null;
  const [activeTab, setActiveTab] = useState<PlanType>(
    navState?.tab === 'institutional' || navState?.tab === 'enterprise' ? navState.tab : 'individual'
  );
  const lockTab = Boolean(navState?.lockTab);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currency, setCurrency] = useState<Currency | null>(null);
  const [detectedCurrencyCode, setDetectedCurrencyCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Detect currency once on mount — use INR for India, USD for everyone else
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://mannaplag.mannaanalytics.com/api';
    fetch(`${apiUrl}/plans/detect-currency/`)
      .then(r => r.json())
      .then((c: any) => {
        const code = c?.code || 'USD';
        setDetectedCurrencyCode(code === 'INR' ? 'INR' : 'USD');
      })
      .catch(() => setDetectedCurrencyCode('USD'));
  }, []);

  // Fetch plans whenever tab or detected currency changes
  useEffect(() => {
    if (detectedCurrencyCode === null) return; // wait for detection
    async function load() {
      setLoading(true);
      try {
        const result = await PlansAPI.list(PLAN_TYPE_MAP[activeTab], 'Annual', detectedCurrencyCode ?? undefined);
        setPlans(result.plans ?? []);
        setCurrency(result?.currency ?? null);
      } catch {
        setPlans([]);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [activeTab, detectedCurrencyCode]);

  const sym = currency?.symbol || '$';
  const filtered = plans;

  return (
    <div>
      <SEO
        title="Plagiarism Checker & AI Detection Pricing Plans"
        description="Transparent, affordable plagiarism checking and AI detection plans for individual researchers, universities, and journal publishers. 3 free checks included."
        canonicalPath="/pricing"
        keywords="plagiarism checker price, turnitin cost without university, academic plagiarism checker free trial, affordable plagiarism checker, institutional plagiarism subscription"
      />
      {/* Header */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <h1 className="text-[36px] font-black text-gray-900 tracking-tight mb-3">Simple, transparent pricing</h1>
          <p className="text-[15px] text-gray-500 font-medium max-w-lg mx-auto">
            Every plan includes a free trial. No credit card required to start.
          </p>
          {currency?.show_gst && (
            <p className="text-[12px] text-gray-400 font-medium mt-2">Prices in INR, exclusive of GST (18%)</p>
          )}
        </div>
      </section>

      {/* Plan type tabs */}
{/* Plan type tabs */}
{/* Plan type tabs */}
      {!lockTab ? (
      <section className="bg-gray-50 border-b border-gray-100 sticky top-16 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 gap-3 flex-wrap">
            <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1">
              {TAB_LABELS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`px-5 py-2 rounded-md text-[13px] font-bold transition-all ${activeTab === t.key
                    ? 'bg-navy text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-800'
                    }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
      ) : (
      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
          <p className="text-[13px] font-black uppercase tracking-widest text-navy">
            {TAB_LABELS.find(t => t.key === activeTab)?.label} account plans
          </p>
        </div>
      </section>
      )}
      {/* Plan cards */}
      <section className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-navy border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className={`grid gap-5 ${filtered.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-2xl mx-auto' :
              filtered.length === 3 ? 'grid-cols-1 md:grid-cols-3' :
                'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
              }`}>
              {filtered.map(plan => (
                <PlanCard
                  key={plan.plan_id}
                  plan={plan}
                  sym={sym}
                  tab={activeTab}
                />
              ))}
            </div>
          )}

          {activeTab === 'institutional' && (
            <p className="text-center text-[12px] text-gray-400 font-medium mt-6">
              All institutional plans are billed annually · Formal invoice provided · Purchase orders accepted
            </p>
          )}
        </div>
      </section>

      {/* Compare all plans */}
      {/* <section className="bg-white border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h2 className="text-[22px] font-black text-gray-900 tracking-tight mb-8 text-center">Compare all plans</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-3 pr-6 font-bold text-gray-500 w-48">Feature</th>
                    <th className="text-center py-3 px-3 font-bold text-gray-700">Free</th>
                    <th className="text-center py-3 px-3 font-bold text-gray-700">Starter</th>
                    <th className="text-center py-3 px-3 font-bold text-gray-700">Pro</th>
                    <th className="text-center py-3 px-3 font-bold text-navy bg-blue-50 rounded-t-lg">Institutional</th>
                    <th className="text-center py-3 px-3 font-bold text-gray-700">Publisher API</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Checks', '1 lifetime', '5/month', '20/month', 'Up to 5,000/yr', 'By word volume'],
                    ['Free trial', '—', '7 days', '7 days', '30 days', '14 days'],
                    ['AI detection', '✓', '✓', '✓', '✓', '✓'],
                    ['Full report', '✗', '✓', '✓', '✓', '✓'],
                    ['LMS integration', '✗', '✗', '✗', '✓', '✗'],
                    ['REST API', '✗', '✗', '✗', '✗', '✓'],
                    ['Priority processing', '✗', '✗', '✓', '✓', '✓'],
                    ['Support', 'None', 'Email', 'Email', 'Phone', 'Dedicated'],
                  ].map(([feature, ...vals]) => (
                    <tr key={feature} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-3 pr-6 font-medium text-gray-700">{feature}</td>
                      {vals.map((v, i) => (
                        <td key={i} className={`py-3 px-3 text-center ${i === 3 ? 'bg-blue-50' : ''} ${v === '✓' ? 'text-green-600 font-bold text-[15px]' :
                          v === '✗' ? 'text-gray-300 font-bold text-[15px]' :
                            'text-gray-600 font-medium'
                          }`}>
                          {v}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section> */}

      {/* FAQ */}
      <section className="bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <h2 className="text-[22px] font-black text-gray-900 tracking-tight mb-8 text-center">Frequently asked questions</h2>
          <div className="flex flex-col gap-5">
            {faq.map(item => (
              <div key={item.q} className="border-b border-gray-100 pb-5">
                <p className="text-[14px] font-black text-gray-900 mb-2 tracking-tight">{item.q}</p>
                <p className="text-[13px] text-gray-500 leading-relaxed font-medium">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}